/**
 * Builds PosId -> multiselect map from legacy ItemsMultiselect on modifier group.
 */
export const buildMultiselectMapFromGroup = (group) => {
  const map = {};
  if (!group?.Items?.length) {
    return map;
  }
  const {Items} = group;
  const flags = group.ItemsMultiselect;

  if (Array.isArray(flags) && flags.length === Items.length) {
    Items.forEach((posId, i) => {
      map[String(posId)] = Boolean(flags[i]);
    });
    return map;
  }

  if (flags && typeof flags === 'object' && !Array.isArray(flags)) {
    Items.forEach((posId) => {
      const id = String(posId);
      map[id] = Boolean(flags[posId] ?? flags[id]);
    });
    return map;
  }

  Items.forEach((posId) => {
    map[String(posId)] = false;
  });
  return map;
};

const readModifierId = (entry) => {
  if (entry && typeof entry === 'object') {
    return entry.ModifierId;
  }
  return entry;
};

export const getMultiQuantityModifierIds = (multiQuantityModifiers) => {
  if (!Array.isArray(multiQuantityModifiers)) {
    return [];
  }

  const seen = new Set();
  const ids = [];

  multiQuantityModifiers.forEach((entry) => {
    const modifierId = readModifierId(entry);
    if (modifierId == null) {
      return;
    }
    const key = String(modifierId);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    ids.push(modifierId);
  });

  return ids;
};

export const idsToMultiQuantityModifiers = (ids) =>
  getMultiQuantityModifierIds(ids).map((modifierId) => ({
    ModifierId: modifierId
  }));

/**
 * Reads MultiQuantityModifiers from API (PascalCase or camelCase).
 */
const readMultiQuantityModifiersFromApi = (group) => {
  if (!group) return null;
  const raw =
    group.MultiQuantityModifiers ?? group.multiQuantityModifiers ?? null;
  if (!Array.isArray(raw)) return null;
  return idsToMultiQuantityModifiers(raw);
};

/**
 * Initial MultiQuantityModifiers for edit: prefer API array, else migrate from ItemsMultiselect.
 */
export const getInitialMultiQuantityModifiers = (group) => {
  if (!group) return [];
  const fromApi = readMultiQuantityModifiersFromApi(group);
  if (fromApi) return fromApi;
  const map = buildMultiselectMapFromGroup(group);
  if (!group.Items?.length) return [];
  return idsToMultiQuantityModifiers(
    group.Items.filter((posId) => map[String(posId)])
  );
};

export const modifiersSelectionKey = (selectedModifierRows) => {
  if (!selectedModifierRows?.length) return '';
  return [...selectedModifierRows.map((m) => m.PosId)]
    .map(String)
    .sort()
    .join('|');
};

/**
 * Prune MultiQuantity ids that are no longer in the group.
 * Append ids only for modifiers that were *newly added* to the group vs previousSelectionKey.
 * When previousSelectionKey is null (first sync after reset), do not append "missing" ids —
 * otherwise a partial list from the server would get every other group member auto-added.
 */
export const computeMultiQuantityAfterSelectionChange = (
  prevMultiModifiers,
  selectedModifierRows,
  previousSelectionKey
) => {
  const ids = selectedModifierRows.map((m) => m.PosId);
  const currentKey = modifiersSelectionKey(selectedModifierRows);
  const prevIds = getMultiQuantityModifierIds(prevMultiModifiers);
  const keptIds = prevIds.filter((id) =>
    ids.some((sid) => String(sid) === String(id))
  );

  if (previousSelectionKey == null) {
    return {
      next: idsToMultiQuantityModifiers(keptIds),
      nextSelectionKey: currentKey
    };
  }

  if (currentKey === previousSelectionKey) {
    return {next: null, nextSelectionKey: currentKey};
  }

  const lastSet = new Set(previousSelectionKey.split('|').filter(Boolean));
  const addedIds = ids.filter((pid) => !lastSet.has(String(pid)));
  const toAdd = addedIds.filter(
    (id) => !prevIds.some((p) => String(p) === String(id))
  );

  return {
    next: idsToMultiQuantityModifiers([...keptIds, ...toAdd]),
    nextSelectionKey: currentKey
  };
};
